// Electron Sync Manager for ACCTOO
// Handles 2-way sync between SQLite and Firebase in Electron environment

import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter
} from 'firebase/firestore';
import { db } from '../firebase.config.js';

class ElectronSyncManager {
  constructor(sqliteManager) {
    this.sqliteManager = sqliteManager;
    this.companyId = null;
    this.deviceId = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.lastSyncTime = null;

    // Listen for online/offline events
    window.addEventListener('online', () => {
      console.log('🌐 Online - Starting sync...');
      this.isOnline = true;
      this.performSync();
    });

    window.addEventListener('offline', () => {
      console.log('📴 Offline - Sync paused');
      this.isOnline = false;
    });
  }

  // Initialize sync manager
  init(companyId, deviceId) {
    this.companyId = companyId;
    this.deviceId = deviceId;
    console.log('🔄 Electron Sync Manager initialized:', { companyId, deviceId });
  }

  // =============================================
  // MAIN SYNC OPERATIONS
  // =============================================

  // Perform full sync
  async performSync() {
    if (!this.isOnline || this.syncInProgress) {
      console.log('⚠️ Sync not available or already in progress');
      return { success: false, message: 'Sync not available or in progress' };
    }

    try {
      this.syncInProgress = true;
      console.log('🔄 Starting 2-way sync...');

      // Step 1: Upload pending changes from SQLite to Firebase
      await this.syncSQLiteToFirebase();

      // Step 2: Download latest changes from Firebase to SQLite
      await this.syncFirebaseToSQLite();

      // Step 3: Resolve any conflicts
      await this.resolveConflicts();

      this.lastSyncTime = new Date();
      console.log('✅ 2-way sync completed successfully');

      return { success: true, message: 'Sync completed successfully' };
    } catch (error) {
      console.error('❌ Sync failed:', error);
      return { success: false, error: error.message };
    } finally {
      this.syncInProgress = false;
    }
  }

  // =============================================
  // SQLITE TO FIREBASE SYNC
  // =============================================

  // Sync pending changes from SQLite to Firebase
  async syncSQLiteToFirebase() {
    try {
      console.log('📤 Syncing SQLite → Firebase...');

      // Get all pending sync records
      const pendingRecords = await this.sqliteManager.select('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId
      });

      const pendingRecordsFiltered = pendingRecords.filter(record => record.status === 'pending');

      console.log(`📊 Found ${pendingRecordsFiltered.length} pending records to sync`);

      for (const record of pendingRecordsFiltered) {
        await this.uploadRecordToFirebase(record);
      }

      console.log('✅ SQLite → Firebase sync completed');
    } catch (error) {
      console.error('❌ SQLite → Firebase sync failed:', error);
      throw error;
    }
  }

  // Upload single record to Firebase
  async uploadRecordToFirebase(record) {
    try {
      const { table_name, record_id, operation, data } = record;
      const recordData = JSON.parse(data);

      // Firebase path: companies/{companyId}/devices/{deviceId}/{table_name}/{record_id}
      const firebasePath = `companies/${this.companyId}/devices/${this.deviceId}/${table_name}/${record_id}`;

      switch (operation) {
        case 'create':
        case 'update':
          await setDoc(doc(db, firebasePath), {
            ...recordData,
            synced_at: new Date(),
            sync_status: 'synced'
          });
          break;

        case 'delete':
          await deleteDoc(doc(db, firebasePath));
          break;

        default:
          console.warn(`⚠️ Unknown operation: ${operation}`);
          return;
      }

      // Mark as synced in SQLite
      await this.sqliteManager.update('sync_queue', record.id, {
        status: 'synced',
        synced_at: new Date().toISOString()
      });

      console.log(`✅ Synced ${operation}: ${table_name}/${record_id}`);
    } catch (error) {
      console.error(`❌ Upload record failed:`, error);

      // Mark as failed and retry later
      await this.sqliteManager.update('sync_queue', record.id, {
        status: 'failed',
        error_message: error.message
      });

      throw error;
    }
  }

  // =============================================
  // FIREBASE TO SQLITE SYNC
  // =============================================

  // Sync latest changes from Firebase to SQLite
  async syncFirebaseToSQLite() {
    try {
      console.log('📥 Syncing Firebase → SQLite...');

      const collections = [
        'parties', 'items', 'sales_bills', 'challan',
        'quotation', 'purchase_bills', 'purchase_orders', 'payments', 'expenses'
      ];

      for (const collectionName of collections) {
        await this.downloadCollectionFromFirebase(collectionName);
      }

      console.log('✅ Firebase → SQLite sync completed');
    } catch (error) {
      console.error('❌ Firebase → SQLite sync failed:', error);
      throw error;
    }
  }

  // Download collection from Firebase
  async downloadCollectionFromFirebase(collectionName) {
    try {
      console.log(`📋 Syncing collection: ${collectionName}`);

      const firebasePath = `companies/${this.companyId}/devices/${this.deviceId}/${collectionName}`;
      const collectionRef = collection(db, firebasePath);

      // Get all documents from Firebase
      const snapshot = await getDocs(collectionRef);

      console.log(`📊 Found ${snapshot.docs.length} ${collectionName} records in Firebase`);

      for (const docSnapshot of snapshot.docs) {
        const firebaseData = docSnapshot.data();
        const recordId = docSnapshot.id;

        // Check if record exists in SQLite
        const existingRecords = await this.sqliteManager.select(collectionName, {
          id: recordId,
          company_id: this.companyId
        });

        if (existingRecords.length === 0) {
          // Insert new record
          await this.sqliteManager.insert(collectionName, {
            ...firebaseData,
            sync_status: 'synced'
          });
          console.log(`✅ Inserted: ${collectionName}/${recordId}`);
        } else {
          // Check if Firebase data is newer
          const localRecord = existingRecords[0];
          if (this.isFirebaseNewer(firebaseData, localRecord)) {
            await this.sqliteManager.update(collectionName, recordId, {
              ...firebaseData,
              sync_status: 'synced'
            });
            console.log(`✅ Updated: ${collectionName}/${recordId}`);
          }
        }
      }

      console.log(`✅ Completed ${collectionName} sync`);
    } catch (error) {
      console.error(`❌ Collection sync failed: ${collectionName}`, error);
      throw error;
    }
  }

  // =============================================
  // CONFLICT RESOLUTION
  // =============================================

  // Resolve sync conflicts
  async resolveConflicts() {
    try {
      console.log('🔍 Checking for conflicts...');

      // Get failed sync records
      const failedRecords = await this.sqliteManager.select('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId
      });

      const failedRecordsFiltered = failedRecords.filter(record => record.status === 'failed');

      if (failedRecordsFiltered.length > 0) {
        console.log(`⚠️ Found ${failedRecordsFiltered.length} failed sync records`);

        // Create conflict report for owner review
        await this.createConflictReport(failedRecordsFiltered);
      }

      console.log('✅ Conflict resolution completed');
    } catch (error) {
      console.error('❌ Conflict resolution failed:', error);
      throw error;
    }
  }

  // Create conflict report
  async createConflictReport(failedRecords) {
    try {
      const reportData = {
        id: `conflict_${Date.now()}`,
        company_id: this.companyId,
        device_id: this.deviceId,
        type: 'sync_conflicts',
        failed_records: failedRecords,
        total_failed: failedRecords.length,
        created_at: new Date().toISOString(),
        status: 'pending_review'
      };

      // Save to SQLite for local access
      await this.sqliteManager.insert('conflict_reports', reportData);

      // Also save to Firebase for owner access
      const firebasePath = `companies/${this.companyId}/conflict_reports/${reportData.id}`;
      await setDoc(doc(db, firebasePath), reportData);

      console.log(`⚠️ Conflict report created: ${reportData.id}`);
    } catch (error) {
      console.error('❌ Create conflict report failed:', error);
      throw error;
    }
  }

  // =============================================
  // UTILITY METHODS
  // =============================================

  // Check if Firebase data is newer than local
  isFirebaseNewer(firebaseData, localData) {
    const firebaseTime = firebaseData.updated_at || firebaseData.synced_at;
    const localTime = localData.updated_at;

    if (!firebaseTime || !localTime) return true;

    const firebaseDate = new Date(firebaseTime.seconds ? firebaseTime.seconds * 1000 : firebaseTime);
    const localDate = new Date(localTime);

    return firebaseDate > localDate;
  }

  // Get sync status
  async getSyncStatus() {
    try {
      const pendingRecords = await this.sqliteManager.select('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId
      });

      const pendingCount = pendingRecords.filter(record => record.status === 'pending').length;
      const failedCount = pendingRecords.filter(record => record.status === 'failed').length;

      return {
        isOnline: this.isOnline,
        syncInProgress: this.syncInProgress,
        lastSyncTime: this.lastSyncTime,
        pendingRecords: pendingCount,
        failedRecords: failedCount
      };
    } catch (error) {
      console.error('❌ Get sync status failed:', error);
      throw error;
    }
  }

  // Manual sync trigger
  async triggerSync() {
    console.log('🔄 Manual sync triggered');
    return await this.performSync();
  }

  // Get sync history
  async getSyncHistory(limit = 50) {
    try {
      const records = await this.sqliteManager.select('sync_queue', {
        company_id: this.companyId,
        device_id: this.deviceId
      });

      return records
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, limit);
    } catch (error) {
      console.error('❌ Get sync history failed:', error);
      throw error;
    }
  }
}

export default ElectronSyncManager;
